import type { ICommitTaskForm } from '@apis/schedule';
import type { IScheduleProfession, ISchedulerTask, TaskKind } from '@interfaces/IScheduler';
import { commitGaps, type CommitGap } from './commitReadiness';

// ---------------------------------------------------------------------------
// The commit review's DRAFT — the promise as the manager is shaping it before
// pressing Commit (2026-09-18 commit UX design, decision 3). Starts from the
// task as saved; the review edits it in place; `draftToForm` sends only what
// the review actually changed, so the commit writes the gaps it filled and
// nothing the plan already had.
// ---------------------------------------------------------------------------

export interface ICommitDraft {
  kind: TaskKind;
  profession: IScheduleProfession | undefined;
  areaIds: string[];
  /** Trusstor username, or null for nobody. */
  assignee: string | null;
  dangerousActivity: boolean;
}

/** What the commit request carries — the shared wire form (`ICommitTaskForm`),
 *  with the assignee always answered: the review defaults it to the caller, and
 *  `null` is an explicit "nobody", never a question left open. */
export type ICommitForm = ICommitTaskForm & { assignee: string | null };

type DraftSource = Pick<ISchedulerTask, 'kind' | 'status' | 'profession' | 'locations' | 'dangerousActivity' | 'assignee'>;

export function draftFromTask(task: DraftSource, defaultAssignee: string | null): ICommitDraft {
  return {
    kind: task.kind,
    profession: task.profession,
    areaIds: (task.locations ?? []).map((location) => location.locationId),
    assignee: task.assignee ?? defaultAssignee,
    dangerousActivity: task.dangerousActivity ?? false,
  };
}

/**
 * Switch the promise's kind. Administrative work has **no areas** — they exist
 * only to become Trusstor area sequence items, and an administrative activity
 * has no Trusstor side — so choosing it drops them rather than hiding them and
 * leaving them on the task (the server enforces the same rule).
 */
export function draftWithKind(draft: ICommitDraft, kind: TaskKind): ICommitDraft {
  return kind === 'administrative' ? { ...draft, kind, areaIds: [] } : { ...draft, kind };
}

/** The gaps the review still has to close, read off the draft (not the task). */
export function draftGaps(draft: ICommitDraft, status: ISchedulerTask['status']): CommitGap[] {
  return commitGaps({
    kind: draft.kind,
    status,
    profession: draft.profession,
    locations: draft.areaIds.map((locationId) => ({ locationId, status: 'unplanned' })),
  });
}

/** The request: the promise's facts, plus only the plan fields the review changed. */
export function draftToForm(draft: ICommitDraft, task: DraftSource): ICommitForm {
  const plan: NonNullable<ICommitForm['plan']> = {};
  if (draft.kind !== task.kind) plan.kind = draft.kind;
  if (draft.profession && draft.profession.professionId !== task.profession?.professionId) plan.profession = draft.profession;
  const saved = (task.locations ?? []).map((location) => location.locationId);
  if (!sameSet(saved, draft.areaIds)) plan.areaIds = draft.areaIds;
  return {
    dangerousActivity: draft.dangerousActivity,
    assignee: draft.assignee,
    ...(Object.keys(plan).length > 0 ? { plan } : {}),
  };
}

function sameSet(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const seen = new Set(a);
  return b.every((id) => seen.has(id));
}
